import { Button, Icon, Text } from '@msc-b2c/shared-components';
import { CartItem, PrepaidConfirmationProps, OBSObjI } from '@msc-b2c/interfaces/types';
import styles from './prepaidpaymentconfirmation.module.scss';
import { navigateOrRedirectToRoute } from '../../../navigationmenu/utils/navigationMenuUtils';
import { useNavigate } from 'react-router-dom';
import SideSummary from '../CartBody/SideSummary/SideSummary';
import { Fragment, useEffect, useRef, useState } from 'react';
import { MyMscExcursion } from '@msc-b2c/interfaces/interfaces';
import { Card } from '../CartBody/Card/Card';
import { getMergedCartDisplayItems } from '../../../utils/myAreaCartUtils';
import {
  getActiveSession,
  getDateFormat,
  replacePlaceholders,
  RootState,
  scrollToTop,
  updateCartItems,
} from '@msc-b2c/shared-utils';
import { LoginMode, NavigationRouteTypeEnum } from '@msc-b2c/interfaces/enums';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { clsx } from 'clsx';

export function PrepaidPaymentConfirmation({
  cartItems,
  confirmation,
  summary,
  card,
  isPricesLoading,
}: Readonly<PrepaidConfirmationProps>) {
  const dispatch = useDispatch();
  const {
    confirmationIcon,
    confirmationTitle,
    confirmationEmailText,
    primaryConfirmationButton,
    secondaryConfirmationButton,
    confirmationDetailsTitle,
    confirmationCheckoutNumber,
    checkoutConfirmationDate,
    confirmationOrderSummaryTitle,
    secondaryButtonEnabledInLightLogin,
    secondaryButtonIsAlreadyPurchased,
  } = confirmation;

  const navigate = useNavigate();
  const { loginMode, email } = getActiveSession();
  const emailForLightMode = useSelector(
    (state: RootState) => state.myAreaCart.loggedUserEmailInput
  );

  const { confirmation: confirmationResponse } = useSelector(
    (state: RootState) => state.myAreaCart
  );
  const isLightLoginMode = loginMode === LoginMode.LIGHT;

  const obsCatalog = useSelector((state: RootState) => state.myCruise?.obs?.onboardServices ?? []);
  const excursionsCatalog = useSelector(
    (state: RootState) => state.myCruise?.excursions?.excursions ?? []
  );
  const catalogSnapshotRef = useRef<(OBSObjI | MyMscExcursion)[] | null>(null);
  if (!catalogSnapshotRef.current && (obsCatalog.length > 0 || excursionsCatalog.length > 0)) {
    catalogSnapshotRef.current = [...obsCatalog, ...excursionsCatalog];
  }

  const { navigationItems } = useSelector((state: RootState) => state.navigationMenuData);

  const isAlreadyPurchasedPageVisibleLL = () => {
    if (loginMode === 'full') return false;
    if (!secondaryButtonEnabledInLightLogin || !secondaryButtonIsAlreadyPurchased) return false;
    if (!secondaryConfirmationButton?.link.valid) return false;
    if (!navigationItems?.length) return false;

    const planMyCruise = navigationItems.find(
      (item) => NavigationRouteTypeEnum.PLAN_MY_CRUISE === item.routeType
    );
    if (!planMyCruise) return false;

    return (planMyCruise.children ?? [])
      .filter(({ urlLink }) => !!urlLink.valid && !!urlLink.url)
      .some(({ urlLink }) => urlLink.url === secondaryConfirmationButton.link.url);
  };

  const [items] = useState<CartItem[]>(() => cartItems);
  useEffect(() => {
    scrollToTop();

    // remove all bought items from cart
    dispatch(
      updateCartItems(
        cartItems.filter(
          (item: CartItem) =>
            ![
              ...(confirmationResponse?.excursions ?? []),
              ...(confirmationResponse?.onboardServices ?? []),
            ].some((boughtItem) => boughtItem.code === item.code)
        )
      )
    );
  }, []);

  return (
    <div className={styles['cmp-confirmation']}>
      <div className={styles['cmp-confirmation__icon-text-container']}>
        {confirmationIcon && (
          <Icon
            icon={confirmationIcon}
            styleOption={'icon-xxlarge'}
            color="#297A00"
          />
        )}

        <div className={styles['text-container']}>
          {confirmationTitle && (
            <Text
              text={confirmationTitle}
              styleOption={'header-m primary-text'}
            />
          )}

          {confirmationEmailText && (
            <Text
              text={replacePlaceholders(confirmationEmailText, {
                checkoutPaymentEmail: loginMode === LoginMode.FULL ? email : emailForLightMode,
              })}
              styleOption="cmp-text__big supporting-text"
            />
          )}
        </div>
      </div>
      {(primaryConfirmationButton || secondaryConfirmationButton) && (
        <div
          className={clsx(styles['cmp-confirmation__button-container'], {
            light: isLightLoginMode && !isAlreadyPurchasedPageVisibleLL(),
          })}
        >
          {primaryConfirmationButton && (
            <Button
              styleOption={
                'cmp-button__' + primaryConfirmationButton.style + ' cmp-button -extended'
              }
              buttonType="button"
              text={primaryConfirmationButton.label}
              onClick={() => {
                if (primaryConfirmationButton.link.url) {
                  globalThis.location.href = primaryConfirmationButton.link.url;
                }
              }}
            />
          )}

          {secondaryConfirmationButton &&
            (isAlreadyPurchasedPageVisibleLL() || !isLightLoginMode) && (
              <Button
                styleOption={
                  'cmp-button__' + secondaryConfirmationButton.style + ' cmp-button -extended'
                }
                buttonType="button"
                text={secondaryConfirmationButton.label}
                onClick={() =>
                  navigateOrRedirectToRoute(secondaryConfirmationButton.link || {}, navigate)
                }
              />
            )}
        </div>
      )}
      {(confirmationDetailsTitle || confirmationCheckoutNumber || checkoutConfirmationDate) && (
        <div className={styles['cmp-confirmation__details']}>
          <section
            className="title-text"
            aria-labelledby="confirmation-section-title"
          >
            {confirmationDetailsTitle && (
              <Text
                text={confirmationDetailsTitle}
                styleOption="header-xs"
              />
            )}
          </section>
          <section
            className="confirmation-number"
            aria-labelledby="confirmation-section-confirmation-number"
          >
            {confirmationCheckoutNumber && (
              <Text
                text={replacePlaceholders(confirmationCheckoutNumber, {
                  checkoutConfirmationNumber: confirmationResponse?.number ?? '',
                })}
                styleOption="body-default"
              />
            )}
          </section>
          <section
            className="confirmation-date"
            aria-labelledby="confirmation-section-confirmation-date"
          >
            {checkoutConfirmationDate && (
              <Text
                text={replacePlaceholders(checkoutConfirmationDate, {
                  checkoutConfirmationDate: dayjs().format(getDateFormat()),
                })}
                styleOption="body-default"
              />
            )}
          </section>
        </div>
      )}
      <div className={clsx(styles['cmp-confirmation__cards'], 'cmp-cartbody__accordion')}>
        {confirmationOrderSummaryTitle && (
          <Text
            text={confirmationOrderSummaryTitle}
            styleOption="header-xs orderSummary-title"
          />
        )}
        {getMergedCartDisplayItems(items ?? []).map(({ parent, related }) => (
          <Fragment
            key={`${parent.itemType}-${parent.code}-${
              parent.startDate ?? parent.deliveryDate ?? 'no-date'
            }-${parent.relatedItemCode ?? 'no-related'}`}
          >
            <hr className={styles['accordion-separator']} />
            <Card
              item={parent}
              relatedItems={related}
              labels={card}
              isPricesLoading={isPricesLoading}
              catalogSnapshot={catalogSnapshotRef.current ?? undefined}
            />
          </Fragment>
        ))}
      </div>
      <SideSummary
        items={items}
        labels={{ ...summary, checkoutButtonText: undefined, ecouponText: undefined }}
        isPricesLoading={isPricesLoading}
      />
    </div>
  );
}
